import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon } from '@luacentrix/icons';
import { cn, mergeIds, variant } from './utils';
import { IconButton } from './primitives';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, htmlFor, children, className }: FieldProps) {
  const errorId = htmlFor === undefined ? undefined : `${htmlFor}-error`;
  const hintId = htmlFor === undefined ? undefined : `${htmlFor}-hint`;
  return (
    <div
      className={cn(
        'lc-field',
        variant('lc-field', { error: error !== undefined && error !== false }),
        className,
      )}
    >
      {label === undefined ? null : (
        <label
          className="lc-field__label"
          htmlFor={htmlFor}
          data-required={required === true ? 'true' : undefined}
        >
          {label}
        </label>
      )}
      {children}
      {hint === undefined ? null : (
        <span id={hintId} className="lc-field__hint">
          {hint}
        </span>
      )}
      {error === undefined || error === false ? null : (
        <span id={errorId} className="lc-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  describedBy?: string;
  errorText?: ReactNode;
  label?: ReactNode;
  hint?: ReactNode;
  icon?: 'search' | 'mail' | 'key' | 'lock' | 'user';
}

export function Input({
  invalid,
  describedBy,
  errorText,
  label,
  hint,
  icon,
  className,
  id,
  required,
  ...rest
}: InputProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const showInvalid = invalid === true || (errorText !== undefined && errorText !== false);

  const control = (
    <input
      id={inputId}
      className={cn('lc-input', className)}
      aria-invalid={showInvalid || undefined}
      aria-describedby={mergeIds(
        describedBy,
        showInvalid ? errorId : undefined,
        hint !== undefined ? hintId : undefined,
      )}
      required={required}
      {...rest}
    />
  );

  if (label === undefined && icon === undefined) return control;

  return (
    <Field
      label={label}
      hint={hint}
      error={errorText}
      htmlFor={inputId}
      required={required === true}
    >
      {icon === undefined ? (
        control
      ) : (
        <span className="lc-input-adorned">
          <span className="lc-input-adorned__icon">
            <Icon name={icon} size="sm" />
          </span>
          {control}
        </span>
      )}
    </Field>
  );
}

export function PasswordInput(props: Omit<InputProps, 'type' | 'icon'>) {
  const [revealed, setRevealed] = useState(false);
  const generated = useId();
  const id = props.id ?? generated;

  return (
    <span className="lc-input-adorned">
      <Input
        {...props}
        id={id}
        type={revealed ? 'text' : 'password'}
        autoComplete={props.autoComplete ?? 'current-password'}
      />
      <span className="lc-input-adorned__action">
        <IconButton
          name={revealed ? 'eyeOff' : 'eye'}
          label={revealed ? 'Hide password' : 'Show password'}
          onClick={() => setRevealed(current => !current)}
          type="button"
        />
      </span>
    </span>
  );
}

export function SearchInput({
  onSearch,
  placeholder = 'Search',
  value,
  onValueChange,
  ...rest
}: Omit<InputProps, 'type' | 'icon' | 'onChange' | 'value'> & {
  value: string;
  onValueChange: (next: string) => void;
  onSearch?: (query: string) => void;
}) {
  const generated = useId();
  const id = rest.id ?? generated;
  return (
    <span className="lc-input-adorned" style={{ width: '100%' }}>
      <span className="lc-input-adorned__icon">
        <Icon name="search" size="sm" />
      </span>
      <input
        {...rest}
        id={id}
        type="search"
        className="lc-input"
        placeholder={placeholder}
        value={value}
        aria-label={rest['aria-label'] ?? placeholder}
        onChange={event => onValueChange(event.currentTarget.value)}
        onKeyDown={event => {
          if (event.key === 'Enter' && onSearch !== undefined) onSearch(value);
        }}
      />
    </span>
  );
}

export function Textarea({
  label,
  hint,
  errorText,
  invalid,
  id,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  errorText?: ReactNode;
  invalid?: boolean;
}) {
  const generated = useId();
  const areaId = id ?? generated;
  const showInvalid = invalid === true || (errorText !== undefined && errorText !== false);
  return (
    <Field
      label={label}
      hint={hint}
      error={errorText}
      htmlFor={areaId}
      required={rest.required === true}
    >
      <textarea
        id={areaId}
        className={cn('lc-input', className)}
        aria-invalid={showInvalid || undefined}
        aria-describedby={showInvalid ? `${areaId}-error` : undefined}
        {...rest}
      />
    </Field>
  );
}

export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  step = 1,
  label,
  hint,
  errorText,
}: {
  value: number;
  onValueChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: ReactNode;
  hint?: ReactNode;
  errorText?: ReactNode;
}) {
  const clamp = (next: number) => {
    const lower = min ?? Number.MIN_SAFE_INTEGER;
    const upper = max ?? Number.MAX_SAFE_INTEGER;
    return Math.max(lower, Math.min(upper, next));
  };
  return (
    <Input
      type="number"
      inputMode="numeric"
      label={label}
      hint={hint}
      errorText={errorText}
      value={String(value)}
      min={min}
      max={max}
      step={step}
      onChange={event => {
        const parsed = Number(event.currentTarget.value);
        if (Number.isFinite(parsed)) onValueChange(clamp(parsed));
      }}
    />
  );
}

export interface RadioOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  disabled?: boolean;
}

export function RadioGroup<T extends string>({
  name,
  options,
  value,
  onValueChange,
  label,
}: {
  name: string;
  options: Array<RadioOption<T>>;
  value: T;
  onValueChange: (next: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="lc-stack lc-stack--tight">
      {options.map(option => (
        <label key={option.value} className="lc-checkbox" style={{ alignItems: 'center' }}>
          <input
            className="lc-checkbox__control"
            style={{ borderRadius: 'var(--lc-radius-pill)' }}
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            disabled={option.disabled === true}
            onChange={() => onValueChange(option.value)}
          />
          <span className="lc-checkbox__label">
            {option.label}
            {option.description === undefined ? null : (
              <span className="lc-field__hint" style={{ display: 'block' }}>
                {option.description}
              </span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}

export function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  label,
}: {
  value: number;
  onValueChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
}) {
  return (
    <label className="lc-field">
      <span className="lc-field__label">
        {label}
        <span style={{ float: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </span>
      <input
        className="lc-input"
        style={{ height: 'auto', padding: 0, border: 0, background: 'transparent' }}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={event => onValueChange(Number(event.currentTarget.value))}
      />
    </label>
  );
}

export function DatePicker({
  value,
  onValueChange,
  label = 'Date',
  min,
  max,
  errorText,
}: {
  value: string;
  onValueChange: (next: string) => void;
  label?: string;
  min?: string;
  max?: string;
  errorText?: ReactNode;
}) {
  return (
    <Input
      type="date"
      label={label}
      value={value}
      min={min}
      max={max}
      errorText={errorText}
      onChange={event => onValueChange(event.currentTarget.value)}
    />
  );
}
